import React, { useMemo, useState } from "react";
import { FormProvider, useForm, Controller } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Fade,
  InputLabel,
  Slide,
  TextField,
} from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Link from "next/link";
import { forgotPassword } from "@api/misc";
import { useRouter } from "next/router";
import { getRandomImage } from "@components/Onboarding/AccountCreation/CreateEmail";
import Image from "next/image";

const schema = yup.object().shape({
  email: yup.string().email().required(),
});

const index = () => {
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [step, setStep] = useState(0);
  const [backgroundImage] = useState(getRandomImage());

  const route = useRouter();
  const form = useForm({
    defaultValues: {
      email: "",
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = (data) => {
    setLoading(true);
    forgotPassword(data.email)
      .then(() => {
        setStep(1);
        setAlert({
          open: true,
          message: "Password reset link sent successfully",
          severity: "success",
        });

        setTimeout(() => {
          route.push("/");
        }, 5000);
      })
      .catch((err) => {
        setAlert({
          open: true,
          message: `Something went wrong! ${err?.response?.data?.result}`,
          severity: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useMemo(() => {
    if (alert.open) {
      setTimeout(() => {
        setAlert({
          ...alert,
          open: false,
        });
      }, 2000);
    }
  }, [alert]);

  const renderForm = () => {
    if (step === 0) {
      return (
        <section className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md mx-auto">
          <h1 className="text-3xl font-bold md:mb-5 mb-1 text-center">
            Enter Your Email Below
          </h1>
          <p className="text-gray-600 text-center">
            We’ll send you a password reset link
          </p>
          <FormProvider {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col items-center w-full mt-14"
            >
              <Controller
                name={"email"}
                control={form.control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Box className="w-full text-left">
                    <InputLabel htmlFor="email-id" className="cursor-pointer">
                      Email
                    </InputLabel>
                    <TextField
                      disabled={loading}
                      id="email-id"
                      size="small"
                      error={!!error}
                      onChange={onChange}
                      value={value}
                      fullWidth
                      variant="outlined"
                      placeholder="Enter your email"
                      autoComplete="email"
                      helperText={error?.message}
                      className="bg-white"
                    />
                  </Box>
                )}
              />
              <Button
                disabled={loading}
                variant="contained"
                size="large"
                sx={{ width: 200, marginTop: 5 }}
                className="capitalize"
                type="submit"
              >
                Send Link
              </Button>
            </form>
          </FormProvider>
        </section>
      );
    } else {
      return (
        <section className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md mx-auto">
          <h1 className="text-3xl font-bold md:mb-5 mb-1">Check Your Email</h1>
          <p className="text-gray-600">We’ve sent you a password reset link</p>
        </section>
      );
    }
  };

  return (
    <div
      className="relative flex min-h-screen w-full flex-col justify-center items-center px-4 md:px-0 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {/* Header with College Aid Pro logo */}
      <Image
        alt="College Aid Pro"
        priority={true}
        src="/images/cap_main_logo_white.png"
        width={255}
        height={170}
        className="absolute top-4 left-4 flex items-center text-white"
      />
      {renderForm()}

      {/* Footer */}
      <div className="absolute bottom-4 left-4 right-4 flex flex-col sm:flex-row justify-between items-center text-white text-xs">
        <span>©{new Date().getFullYear()} College Aid Pro Inc.</span>
        <Link href="/Privacy Policy.pdf" target="_blank">
          <span className="text-white cursor-pointer">Privacy Policy</span>
        </Link>
      </div>
      <Fade in={alert.open} timeout={600}>
        <div>
          <Slide direction="up" in={alert.open} timeout={600}>
            <Alert
              variant="outlined"
              severity={alert.severity}
              className="fixed bottom-16 right-2"
            >
              {alert.message}
            </Alert>
          </Slide>
        </div>
      </Fade>
    </div>
  );
};

export default index;
